import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useRouter } from '@tanstack/react-router'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { useToast } from '@/components/ui/Toast'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { Sheet } from '@/components/ui/Sheet'
import { StatusChip } from '@/components/ui/StatusChip'
import { OwnerDeleteButton } from '@/features/owner/OwnerDelete'
import { useProfile } from '@/auth/SessionProvider'
import { canWriteDepartment } from '@/auth/profile'
import {
  addBudgetLine, approveBudget, discardDraftBudget, getBudget, listBudgetActuals, listBudgetLines, listCategories,
  rejectBudget, removeBudgetLine, reopenAsDraft, reviseApprovedBudget, submitBudget, updateBudgetLine,
  withdrawBudget,
} from '@/data/budgets'
import type { Budget, BudgetLine } from '@/data/budgets'
import { formatKwacha } from '@/lib/money'
import { percent } from '@/lib/reportMath'
import { amountToNgwee, lineFormSchema, quarterLabel, reasonSchema } from './schemas'

const newRef = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `x-${Date.now()}-${Math.random().toString(36).slice(2)}`)
const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')
/** Whole Kwacha when there are no ngwee, so the edit box shows "500" not "500.00". */
const toInput = (ngwee: number) => (ngwee % 100 === 0 ? String(ngwee / 100) : (ngwee / 100).toFixed(2))

type Confirm = 'submit' | 'withdraw' | 'discard' | 'approve' | null
type Reason = 'reject' | 'revise' | null

export function BudgetDetailPage({ deptId, quarterId }: { deptId: string; quarterId: string }) {
  const profile = useProfile()
  const canWrite = canWriteDepartment(profile, deptId)
  const isAdmin = profile.isAdmin
  const router = useRouter()
  const qc = useQueryClient()

  const budgetQ = useQuery({ queryKey: ['budget', deptId, quarterId], queryFn: () => getBudget(deptId, quarterId) })
  const budget = budgetQ.data ?? null
  const linesQ = useQuery({
    queryKey: ['budget-lines', budget?.id],
    queryFn: () => listBudgetLines(budget!.id),
    enabled: Boolean(budget),
  })
  const catsQ = useQuery({ queryKey: ['categories'], queryFn: listCategories })
  const actualsQ = useQuery({
    queryKey: ['budget-actuals', budget?.id],
    queryFn: () => listBudgetActuals(budget!.id),
    enabled: budget?.status === 'approved',
  })

  const toast = useToast()
  const [actionError, setActionError] = useState('')
  const [confirm, setConfirm] = useState<Confirm>(null)
  const [reasonFor, setReasonFor] = useState<Reason>(null)
  const [reason, setReason] = useState('')
  const [reasonError, setReasonError] = useState('')

  // Line form (add when editing === null)
  const [lineSheet, setLineSheet] = useState(false)
  const [editing, setEditing] = useState<BudgetLine | null>(null)
  const [removing, setRemoving] = useState<BudgetLine | null>(null)
  const [lCat, setLCat] = useState('')
  const [lDesc, setLDesc] = useState('')
  const [lAmount, setLAmount] = useState('')
  const [lNote, setLNote] = useState('')
  const [lErrors, setLErrors] = useState<Record<string, string>>({})
  const [lFormError, setLFormError] = useState('')
  const lineBusy = useRef(false)
  const lineRef = useRef(newRef())

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['budget', deptId, quarterId] })
    await qc.invalidateQueries({ queryKey: ['budget-lines'] })
    await qc.invalidateQueries({ queryKey: ['budget-actuals'] })
    await qc.invalidateQueries({ queryKey: ['budgets', deptId] })
  }

  const saveLineM = useMutation({
    mutationFn: (v: { categoryId: string; description: string; amountNgwee: number; note: string }) =>
      editing ? updateBudgetLine(editing.id, v) : addBudgetLine(budget as Budget, v, lineRef.current),
  })
  const removeLineM = useMutation({ mutationFn: removeBudgetLine })
  const actionM = useMutation({
    mutationFn: async (a: { kind: Exclude<Confirm, null> | 'reject' | 'revise' | 'edit-again'; reason?: string }) => {
      const id = (budget as Budget).id
      switch (a.kind) {
        case 'submit': return submitBudget(id)
        case 'withdraw': return withdrawBudget(id)
        case 'discard': return discardDraftBudget(id)
        case 'approve': return approveBudget(id)
        case 'reject': return rejectBudget(id, a.reason ?? '')
        case 'revise': return reviseApprovedBudget(id, a.reason ?? '')
        case 'edit-again': return reopenAsDraft(id)
      }
    },
  })

  const lines = linesQ.data ?? []
  const cats = catsQ.data ?? []
  const total = lines.reduce((s, l) => s + l.amountNgwee, 0)
  const status = budget?.status
  const actualOf = new Map((actualsQ.data ?? []).map((a) => [a.lineId, a]))
  const sum = (f: (a: { actualNgwee: number; committedNgwee: number; remainingNgwee: number }) => number) =>
    (actualsQ.data ?? []).reduce((t, a) => t + f(a), 0)
  const editable = canWrite && status === 'draft'

  const openLine = (l: BudgetLine | null) => {
    setEditing(l)
    setLCat(l?.categoryId ?? '')
    setLDesc(l?.description ?? '')
    setLAmount(l ? toInput(l.amountNgwee) : '')
    setLNote(l?.note ?? '')
    setLErrors({})
    setLFormError('')
    lineRef.current = newRef()
    setLineSheet(true)
  }

  const submitLine = async (e: FormEvent) => {
    e.preventDefault()
    if (lineBusy.current) return
    setLFormError('')
    const parsed = lineFormSchema.safeParse({ categoryId: lCat, description: lDesc, amount: lAmount, note: lNote })
    if (!parsed.success) {
      const errs: Record<string, string> = {}
      for (const i of parsed.error.issues) errs[String(i.path[0])] ??= i.message
      setLErrors(errs)
      return
    }
    setLErrors({})
    const v = parsed.data
    lineBusy.current = true
    try {
      await saveLineM.mutateAsync({ categoryId: v.categoryId, description: v.description, amountNgwee: amountToNgwee(v.amount) as number, note: v.note })
      await refresh()
      setLineSheet(false)
      toast.success(editing ? 'The line was changed.' : 'The line was added.')
    } catch (err) {
      setLFormError(errText(err))
    } finally {
      lineBusy.current = false
    }
  }

  const confirmRemoveLine = async () => {
    if (!removing || removeLineM.isPending) return
    setActionError('')
    try {
      await removeLineM.mutateAsync(removing.id)
      await refresh()
      setRemoving(null)
      toast.success('The line was removed.')
    } catch (err) {
      setActionError(errText(err))
    }
  }

  const run = async (kind: Parameters<typeof actionM.mutateAsync>[0]['kind'], reasonText?: string) => {
    if (actionM.isPending) return
    setActionError('')
    try {
      await actionM.mutateAsync({ kind, reason: reasonText })
      if (kind === 'discard') {
        await qc.invalidateQueries({ queryKey: ['budgets', deptId] })
        await router.navigate({ to: '/d/$deptId/budgets', params: { deptId } })
        return
      }
      await refresh()
      const fresh = await getBudget(deptId, quarterId)
      toast.success(
        {
          submit: fresh?.status === 'approved' ? 'Sent. No approval was needed, so the budget is approved.' : 'Sent to central treasury for approval.',
          withdraw: 'You took the budget back. You can edit it and send it again.',
          approve: 'The budget is approved.',
          reject: 'The budget was sent back with your reason.',
          revise: 'The budget is open for changes again.',
          'edit-again': 'You can edit the budget again.',
          discard: '',
        }[kind],
      )
      setConfirm(null)
      setReasonFor(null)
    } catch (err) {
      setActionError(errText(err))
      void refresh()
    }
  }

  const submitReason = (e: FormEvent) => {
    e.preventDefault()
    const parsed = reasonSchema.safeParse({ reason })
    if (!parsed.success) {
      setReasonError(parsed.error.issues[0].message)
      return
    }
    setReasonError('')
    if (reasonFor) void run(reasonFor, parsed.data.reason)
  }

  const openConfirm = (c: Exclude<Confirm, null>) => { setActionError(''); setConfirm(c) }
  const openReason = (r: Exclude<Reason, null>) => { setActionError(''); setReason(''); setReasonError(''); setReasonFor(r) }

  if (budgetQ.isPending) {
    return <SkeletonRows rows={4} />
  }
  if (budgetQ.isError) {
    return (
      <div>
        <p role="alert" className="text-body text-bad">{errText(budgetQ.error)}</p>
        <Button variant="secondary" size="lg" className="mt-3" onClick={() => void budgetQ.refetch()}>Try again</Button>
      </div>
    )
  }
  if (!budget) {
    return (
      <EmptyState
        title="No budget for this quarter"
        description={quarterLabel(quarterId)}
        action={<Link to="/d/$deptId/budgets" params={{ deptId }} className="text-brand hover:underline">Back to budgets</Link>}
      />
    )
  }

  const busy = actionM.isPending
  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/d/$deptId/budgets" params={{ deptId }} className="text-body text-brand hover:underline">Back to budgets</Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h2 className="text-title font-semibold text-ink">{quarterLabel(quarterId)}</h2>
          {status && <StatusChip status={status} />}
          {budget.revision > 1 && <span className="text-body text-muted">Revision {budget.revision}</span>}
        </div>
      </div>

      {actionError && !confirm && !reasonFor && <p role="alert" className="rounded-control bg-bad-tint px-4 py-3 text-body text-bad">{actionError}</p>}

      {(status === 'rejected' || (status === 'draft' && budget.decisionNote)) && budget.decisionNote && (
        <p className="rounded-control bg-wait-tint px-4 py-3 text-body text-wait">
          <span className="font-medium">{status === 'rejected' ? 'Sent back by central treasury: ' : 'Reason it was reopened: '}</span>
          {budget.decisionNote}
        </p>
      )}
      {status === 'submitted' && (
        <p className="rounded-control bg-sky-50 px-4 py-3 text-body text-sky-900">Waiting for central treasury to approve. It cannot be edited while it waits.</p>
      )}
      {status === 'approved' && (
        <p className="rounded-control bg-page px-4 py-3 text-body text-neutral">
          Approved{budget.approvedVia === 'central' ? ' by central treasury' : ' (no approval was needed)'}. The lines are fixed.
        </p>
      )}

      {status === 'approved' && actualsQ.data && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" aria-label="Budget against spending">
          {[
            ['Budgeted', total],
            ['Spent', sum((a) => a.actualNgwee)],
            ['Waiting or approved', sum((a) => a.committedNgwee)],
            [sum((a) => a.remainingNgwee) < 0 ? 'Over budget by' : 'Left to spend', Math.abs(sum((a) => a.remainingNgwee))],
          ].map(([label, value]) => (
            <Card key={String(label)}>
              <CardBody>
                <p className="text-body text-muted">{label}</p>
                <p className={`text-title font-semibold ${label === 'Over budget by' ? 'text-bad' : 'text-ink'}`}>{formatKwacha(value as number)}</p>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <CardHeader className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold text-ink">What the money is for</h3>
            <p className="text-body text-muted">Total: <span className="font-semibold text-ink">{formatKwacha(total)}</span></p>
          </div>
          {editable && <Button size="lg" onClick={() => openLine(null)}>Add a line</Button>}
        </CardHeader>
        <CardBody>
          {linesQ.isPending ? (
            <SkeletonRows rows={4} />
          ) : linesQ.isError ? (
            <div>
              <p role="alert" className="text-body text-bad">{errText(linesQ.error)}</p>
              <Button variant="secondary" size="lg" className="mt-3" onClick={() => void linesQ.refetch()}>Try again</Button>
            </div>
          ) : lines.length === 0 ? (
            <EmptyState title="No lines yet" description={editable ? 'Add what you plan to spend on, one line at a time.' : 'Nothing has been added.'} />
          ) : (
            <ul className="divide-y divide-line">
              {lines.map((l) => (
                <li key={l.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="font-medium text-ink">{l.description}</p>
                    <p className="text-body text-muted">{l.categoryName}</p>
                    {l.note && <p className="text-micro text-muted">{l.note}</p>}
                    {status === 'approved' && actualOf.get(l.id) && (() => {
                      const a = actualOf.get(l.id)!
                      const used = a.actualNgwee + a.committedNgwee
                      const pct = percent(used, a.budgetedNgwee)
                      const over = a.remainingNgwee < 0
                      return (
                        <div className="mt-2 max-w-sm">
                          <div className="h-2 overflow-hidden rounded-full bg-neutral-tint" role="img" aria-label={`${pct}% of this line is used`}>
                            <div className={`h-full ${over ? 'bg-bad' : 'bg-brand'}`} style={{ width: `${pct}%` }} />
                          </div>
                          <p className={`mt-1 text-micro ${over ? 'text-bad' : 'text-muted'}`}>
                            Spent {formatKwacha(a.actualNgwee)}
                            {a.committedNgwee !== 0 ? `, waiting or approved ${formatKwacha(a.committedNgwee)}` : ''}
                            {over ? `, over by ${formatKwacha(-a.remainingNgwee)}` : `, ${formatKwacha(a.remainingNgwee)} left`}
                          </p>
                        </div>
                      )
                    })()}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <span className="font-medium text-ink">{formatKwacha(l.amountNgwee)}</span>
                    {editable && (
                      <>
                        <button type="button" onClick={() => openLine(l)} className="inline-flex min-h-[44px] items-center rounded-control px-3 text-body font-medium text-brand hover:bg-neutral-tint">Edit</button>
                        <button type="button" onClick={() => { setActionError(''); setRemoving(l) }} className="inline-flex min-h-[44px] items-center rounded-control px-3 text-body font-medium text-bad hover:bg-neutral-tint">Remove</button>
                      </>
                    )}
                    <OwnerDeleteButton table="budget_line" id={l.id} what="budget line" />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <div className="flex flex-wrap gap-3">
        <OwnerDeleteButton
          table="budget"
          id={budget.id}
          what="budget"
          onDeleted={() => void router.navigate({ to: '/d/$deptId/budgets', params: { deptId } })}
        />
        {canWrite && status === 'draft' && (
          <>
            <Button size="lg" onClick={() => openConfirm('submit')} disabled={lines.length === 0}>Send for approval</Button>
            <Button size="lg" variant="secondary" onClick={() => openConfirm('discard')}>Discard this draft</Button>
          </>
        )}
        {canWrite && status === 'submitted' && (
          <Button size="lg" variant="secondary" onClick={() => openConfirm('withdraw')}>Take it back</Button>
        )}
        {canWrite && (status === 'rejected' || status === 'withdrawn') && (
          <Button size="lg" isLoading={busy} onClick={() => void run('edit-again')}>Edit and send again</Button>
        )}
        {isAdmin && status === 'submitted' && (
          <>
            <Button size="lg" onClick={() => openConfirm('approve')}>Approve</Button>
            <Button size="lg" variant="danger" onClick={() => openReason('reject')}>Send back</Button>
          </>
        )}
        {isAdmin && status === 'approved' && (
          <Button size="lg" variant="secondary" onClick={() => openReason('revise')}>Reopen for changes</Button>
        )}
      </div>

      <Sheet isOpen={lineSheet} onClose={() => setLineSheet(false)} title={editing ? 'Change this line' : 'Add a line'}>
        <form onSubmit={(e) => void submitLine(e)} className="flex flex-col gap-4" noValidate>
          <Select
            label="Category"
            value={lCat}
            onChange={(e) => setLCat(e.target.value)}
            options={[{ value: '', label: 'Choose a category' }, ...cats.map((c) => ({ value: c.id, label: c.name }))]}
            error={lErrors.categoryId}
          />
          <Input label="What is it for?" placeholder="Hall hire for the youth camp" value={lDesc} onChange={(e) => setLDesc(e.target.value)} error={lErrors.description} />
          <Input label="Amount in Kwacha" inputMode="decimal" placeholder="500.00" value={lAmount} onChange={(e) => setLAmount(e.target.value)} error={lErrors.amount} />
          <Input label="Note (optional)" value={lNote} onChange={(e) => setLNote(e.target.value)} error={lErrors.note} />
          {lFormError && <p role="alert" className="text-body text-bad">{lFormError}</p>}
          <Button type="submit" size="lg" isLoading={saveLineM.isPending}>{saveLineM.isPending ? 'Saving...' : 'Save line'}</Button>
        </form>
      </Sheet>

      <Sheet isOpen={removing !== null} onClose={() => setRemoving(null)} title="Remove this line?">
        <div className="flex flex-col gap-4">
          <p className="text-neutral">{removing?.description}, {removing ? formatKwacha(removing.amountNgwee) : ''}.</p>
          {actionError && <p role="alert" className="text-body text-bad">{actionError}</p>}
          <Button variant="danger" size="lg" isLoading={removeLineM.isPending} onClick={() => void confirmRemoveLine()}>Remove line</Button>
          <Button variant="secondary" size="lg" onClick={() => setRemoving(null)}>Keep line</Button>
        </div>
      </Sheet>

      <Sheet isOpen={confirm !== null} onClose={() => setConfirm(null)} title={{ submit: 'Send for approval?', withdraw: 'Take the budget back?', discard: 'Discard this draft?', approve: 'Approve this budget?' }[confirm ?? 'submit']}>
        <div className="flex flex-col gap-4">
          <p className="text-neutral">
            {confirm === 'submit' && `Total ${formatKwacha(total)}. Once sent, the lines cannot be changed unless you take it back or it is sent back to you.`}
            {confirm === 'withdraw' && 'Central treasury will no longer see it as waiting. You can edit it and send it again.'}
            {confirm === 'discard' && 'The draft and its lines are deleted. Nothing else is affected.'}
            {confirm === 'approve' && `${quarterLabel(quarterId)}, total ${formatKwacha(total)}. After this the department can spend against it.`}
          </p>
          {actionError && <p role="alert" className="text-body text-bad">{actionError}</p>}
          <Button
            variant={confirm === 'discard' ? 'danger' : 'primary'}
            size="lg"
            isLoading={busy}
            onClick={() => confirm && void run(confirm)}
          >
            {{ submit: 'Send for approval', withdraw: 'Take it back', discard: 'Discard draft', approve: 'Approve' }[confirm ?? 'submit']}
          </Button>
          <Button variant="secondary" size="lg" onClick={() => setConfirm(null)}>Cancel</Button>
        </div>
      </Sheet>

      <Sheet isOpen={reasonFor !== null} onClose={() => setReasonFor(null)} title={reasonFor === 'reject' ? 'Send this budget back' : 'Reopen this budget'}>
        <form onSubmit={submitReason} className="flex flex-col gap-4" noValidate>
          <Textarea
            label={reasonFor === 'reject' ? 'What needs to change?' : 'Why does it need to change?'}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={reasonError}
            rows={4}
          />
          {actionError && <p role="alert" className="text-body text-bad">{actionError}</p>}
          <Button type="submit" size="lg" variant={reasonFor === 'reject' ? 'danger' : 'primary'} isLoading={busy}>
            {reasonFor === 'reject' ? 'Send back' : 'Reopen'}
          </Button>
        </form>
      </Sheet>
    </div>
  )
}
